import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import toast from 'react-hot-toast';
import { Plus } from 'lucide-react';
import { RecordActions } from '../components/common/RecordActions';
import { RecordViewDialog } from '../components/common/RecordViewDialog';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const ProcurementType = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('procurement_types');
  const [viewItem, setViewItem] = useState(null);
  const [procurementTypes, setProcurementTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);

  useEffect(() => {
    loadProcurementTypes();
  }, []);

  const loadProcurementTypes = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/procurement-types`);
      setProcurementTypes(response.data || []);
    } catch (error) {
      console.error('Error loading procurement types:', error);
      toast.error('Failed to load procurement types');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDialog = (type = null) => {
    setEditingType(type);
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const data = {
      procurement_type_id: (formData.get('procurement_type_id') || '').toUpperCase(),
      description: formData.get('description')
    };

    try {
      if (editingType) {
        await axios.put(`${BACKEND_URL}/api/procurement-types/${editingType.procurement_type_id}`, {
          description: data.description
        });
        toast.success('Procurement Type updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/procurement-types`, data);
        toast.success('Procurement Type created successfully');
      }
      setIsDialogOpen(false);
      loadProcurementTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save procurement type');
    }
  };

  return (
    <Layout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Procurement Types</h1>
            <p className="text-sm text-gray-500 mt-1">Manage procurement type classifications</p>
          </div>
        </div>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Procurement Types</h2>
            {writable && (<Button onClick={() => handleOpenDialog()}>
              <Plus className="w-4 h-4 mr-2" />
              Add Procurement Type
            </Button>)}
          </div>

          {isLoading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
              <p className="mt-2 text-gray-500">Loading...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Procurement Type ID</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Description</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {procurementTypes.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="text-center py-8 text-gray-500">
                        No procurement types found. Click &quot;Add Procurement Type&quot; to create one.
                      </td>
                    </tr>
                  ) : (
                    procurementTypes.map((type) => (
                      <tr key={type.procurement_type_id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4 font-mono text-sm font-semibold">{type.procurement_type_id}</td>
                        <td className="py-3 px-4 text-sm">{type.description}</td>
                        <td className="py-3 px-4 text-right">
                          <RecordActions doctype="procurement_types" onView={() => setViewItem(type)} onEdit={() => handleOpenDialog(type)} />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingType ? 'Edit Procurement Type' : 'Add Procurement Type'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Procurement Type ID * (E, F, or X)</Label>
                  <Input
                    name="procurement_type_id"
                    defaultValue={editingType?.procurement_type_id || ''}
                    placeholder="E, F, or X"
                    maxLength={1}
                    minLength={1}
                    pattern="[EFXefx]"
                    title="Must be E (In-house), F (External), or X (Both)"
                    disabled={!!editingType}
                    className="uppercase"
                    required
                    onInput={(e) => {
                      e.target.value = e.target.value.toUpperCase().replace(/[^EFX]/g, '').slice(0, 1);
                    }}
                  />
                  <p className="text-xs text-gray-500 mt-1">E = In-house production, F = External procurement, X = Both</p>
                </div>
                <div>
                  <Label>Description *</Label>
                  <Input
                    name="description"
                    defaultValue={editingType?.description || ''}
                    placeholder="e.g., In-house production, External procurement"
                    required
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 justify-end pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editingType ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title="Details"
        record={viewItem}
      />
    </Layout>
  );
};

export default ProcurementType;
